import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { opsApi } from '@/api/ops';
import type { CatalogRow, CatalogView } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { Select } from '@/components/Select';
import { StatusChip } from '@/components/ops/Badges';
import { CheckboxField, TextAreaField, TextField } from '@/components/ops/Forms';
import { PageHeader, Section } from '@/components/ops/Layout';
import { Modal, ConfirmDialog } from '@/components/ops/Modal';
import { EnvironmentPicker } from '@/components/ops/Pickers';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { MutationError, QueryState } from '@/components/ops/Problem';
import { formatTimestamp } from '@/ops/format';
import { REPLICA } from '@/ops/live';
import { useSelectedEnvironment } from '@/ops/useEnvironment';

const STEP_KEY = /^[a-z][a-z0-9_.]{1,63}$/;
// Mirrors DefaultStepCatalog.RequiredKeys — the owner's invitation is required since 2026-10-01.
// A deletion's safety rows (a second owner, the backup, the typed confirmation) and IAM's removal phases can never be
// dropped or disabled — the server enforces the same list.
const REQUIRED_KEYS = [
  'iam.create', 'verify.iam', 'iam.founder_invite',
  'delete.approval', 'delete.freeze', 'delete.backup', 'delete.confirm', 'delete.purge_registry', 'delete.purge_drop',
  'discard.freeze', 'discard.purge_registry', 'discard.purge_drop',
];
const KINDS = [
  'iam_create', 'http', 'industry', 'gateway_hosts', 'credits_grant', 'parity', 'welcome', 'founder_invite',
  'approval', 'host_task', 'credits_snapshot', 'iam_deprovision', 'parity_absent', 'console_finalize',
];
const KEY_HEADERS = ['X-Internal-Api-Key', 'X-Admin-Api-Key'];

/** Client-side mirror of the catalog rules (docs §4.4) — the API re-validates. */
export function catalogErrors(rows: CatalogRow[]): string[] {
  const errors: string[] = [];
  const keys = new Set<string>();
  for (const row of rows) {
    if (!STEP_KEY.test(row.key)) errors.push(`${row.key || '(empty)'}: key must match ^[a-z][a-z0-9_.]{1,63}$.`);
    if (keys.has(row.key)) errors.push(`${row.key}: duplicate key.`);
    keys.add(row.key);
    if (row.timeout_seconds < 5 || row.timeout_seconds > 900) errors.push(`${row.key}: timeout must be 5–900 s.`);
    if (row.key_header && !KEY_HEADERS.includes(row.key_header)) errors.push(`${row.key}: key header must be X-Internal-Api-Key or X-Admin-Api-Key.`);
  }
  for (const required of REQUIRED_KEYS) {
    const row = rows.find((r) => r.key === required);
    if (!row) errors.push(`${required} must be present.`);
    else if (!row.enabled) errors.push(`${required} must stay enabled.`);
  }
  return errors;
}

/**
 * The environment's step catalog — the seed script's provision list as DATA (plan §4.1). Anyone who
 * provisions can read it; only an owner with environments:manage edits it (enable a row once a
 * service's endpoint ships, change a timeout, reorder). Saving replaces the whole catalog.
 */
export function StepCatalogPage() {
  const { env, setEnv, environments } = useSelectedEnvironment();
  const catalog = useQuery({
    queryKey: ['ops', 'catalog', env],
    queryFn: () => opsApi.provisioning.catalog(env!),
    enabled: !!env,
    ...REPLICA,
  });
  const canEdit = hasPermission(PERMISSIONS.environmentsManage);
  const [draft, setDraft] = useState<CatalogRow[] | null>(null);

  return (
    <RequirePermission permission={PERMISSIONS.tenantProvision} what="The step catalog">
      <PageHeader
        back={{ to: '/ops/provisioning', label: 'Provisioning' }}
        title="Step catalog"
        subtitle="What a provisioning job runs on this environment, in order. Copied from the default when the environment was created."
        actions={<EnvironmentPicker env={env} environments={environments} onChange={(k) => { setEnv(k); setDraft(null); }} />}
      />
      {env && (
        <QueryState query={catalog}>
          {(view) => draft
            ? <CatalogEditor view={view} rows={draft} onChange={setDraft} onDone={() => setDraft(null)} />
            : <CatalogTable view={view} onEdit={canEdit ? () => setDraft(view.steps.map((s) => ({ ...s }))) : undefined} />}
        </QueryState>
      )}
    </RequirePermission>
  );
}

function CatalogTable({ view, onEdit }: { view: CatalogView; onEdit?: () => void }) {
  return (
    <Section title={`${view.environment} — ${view.steps.length} steps`}
      meta={`template variables: ${view.template_vocabulary.map((v) => `{${v}}`).join(' ')}`}
      actions={onEdit && <button type="button" className="btn btn-ghost btn-sm" onClick={onEdit}>Edit catalog</button>} flush>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr><th className="num">#</th><th>Step</th><th>Call</th><th>Runs for</th><th>State</th><th className="num">Timeout</th><th>Updated</th></tr>
          </thead>
          <tbody>
            {[...view.steps].sort((a, b) => a.ordinal - b.ordinal).map((row) => (
              <tr key={row.key} className={row.enabled ? undefined : 'row-muted'}>
                <td className="num">{row.ordinal}</td>
                <td>
                  <span className="mono" style={{ fontWeight: 600 }}>{row.key}</span>
                  <div className="t-caption muted">{row.kind}{row.app ? ` · ${row.app}` : ''}</div>
                  {row.notes && <div className="t-caption subtle wrap">{row.notes}</div>}
                </td>
                <td className="t-caption mono wrap" style={{ maxWidth: 380 }}>
                  {row.method ?? ''} {row.url_template ?? '—'}
                  {row.key_header && <div className="muted">{row.key_header}</div>}
                </td>
                <td className="t-caption">{(row.job_kinds ?? []).join(', ') || 'all'}</td>
                <td>
                  <StatusChip status={row.enabled ? 'enabled' : 'disabled'} />
                  {row.skip_if_absent && <div className="t-caption muted">skip if the service is absent</div>}
                </td>
                <td className="num">{row.timeout_seconds}s</td>
                <td className="t-caption muted">{row.updated_at ? formatTimestamp(row.updated_at) : '—'}{row.updated_by && <div>{row.updated_by}</div>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function CatalogEditor({
  view, rows, onChange, onDone,
}: {
  view: CatalogView;
  rows: CatalogRow[];
  onChange: (rows: CatalogRow[]) => void;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [confirming, setConfirming] = useState(false);
  const errors = catalogErrors(rows);

  const save = useMutation({
    mutationFn: () => opsApi.provisioning.putCatalog(view.environment, rows),
    onSuccess: (updated) => {
      setConfirming(false);
      queryClient.setQueryData(['ops', 'catalog', view.environment], updated);
      onDone();
    },
  });

  const sorted = rows.map((r, i) => ({ r, i })).sort((a, b) => a.r.ordinal - b.r.ordinal);

  return (
    <Section title={`Editing ${view.environment}`} meta="saving replaces the whole catalog"
      actions={
        <>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onDone} disabled={save.isPending}>Discard</button>
          <button type="button" className="btn btn-primary btn-sm" disabled={errors.length > 0 || save.isPending} onClick={() => setConfirming(true)}>
            Save catalog…
          </button>
        </>
      } flush>
      {confirming && (
        <ConfirmDialog title={`Replace the setup steps of ${view.environment}?`} confirmLabel="Save catalog" pending={save.isPending}
          onConfirm={() => save.mutate()} onCancel={() => { setConfirming(false); save.reset(); }}>
          <p className="t-body" style={{ margin: 0 }}>
            Every new tenant setup on {view.environment} follows these {rows.length} steps from now on. Setups already running keep the
            steps they started with.
          </p>
          <MutationError error={save.error} />
        </ConfirmDialog>
      )}
      {errors.length > 0 && (
        <div className="card-body">
          <ul className="problem-list t-caption note-error">{errors.map((e) => <li key={e}>{e}</li>)}</ul>
        </div>
      )}
      <div className="table-wrap">
        <table className="table">
          <thead><tr><th className="num">#</th><th>Step</th><th>Enabled</th><th className="num">Timeout</th><th aria-label="Actions" /></tr></thead>
          <tbody>
            {sorted.map(({ r, i }) => (
              <tr key={i}>
                <td className="num">{r.ordinal}</td>
                <td><span className="mono">{r.key}</span><div className="t-caption muted">{r.kind}{r.app ? ` · ${r.app}` : ''}</div></td>
                <td>
                  <input type="checkbox" aria-label={`Enable ${r.key}`} checked={r.enabled}
                    onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...x, enabled: e.target.checked } : x)))} />
                </td>
                <td className="num">{r.timeout_seconds}s</td>
                <td>
                  <div className="row">
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(i)}>Edit</button>
                    {!REQUIRED_KEYS.includes(r.key) && (
                      <button type="button" className="btn btn-ghost btn-sm is-danger" onClick={() => onChange(rows.filter((_, j) => j !== i))}>Remove</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card-body">
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing('new')}>Add step</button>
        <MutationError error={save.error} />
      </div>
      {editing !== null && (
        <RowForm
          row={editing === 'new' ? null : rows[editing]!}
          vocabulary={view.template_vocabulary}
          onClose={() => setEditing(null)}
          onSave={(row) => {
            onChange(editing === 'new' ? [...rows, row] : rows.map((x, j) => (j === editing ? row : x)));
            setEditing(null);
          }}
        />
      )}
    </Section>
  );
}

function RowForm({
  row, vocabulary, onClose, onSave,
}: {
  row: CatalogRow | null;
  vocabulary: string[];
  onClose: () => void;
  onSave: (row: CatalogRow) => void;
}) {
  const [ordinal, setOrdinal] = useState(String(row?.ordinal ?? 500));
  const [key, setKey] = useState(row?.key ?? '');
  const [kind, setKind] = useState(row?.kind ?? 'http');
  const [app, setApp] = useState(row?.app ?? '');
  const [method, setMethod] = useState(row?.method ?? 'POST');
  const [url, setUrl] = useState(row?.url_template ?? '');
  const [body, setBody] = useState(row?.body_template ? JSON.stringify(row.body_template, null, 2) : '');
  const [enabled, setEnabled] = useState(row?.enabled ?? true);
  const [skip, setSkip] = useState(row?.skip_if_absent ?? true);
  const [jobKinds, setJobKinds] = useState((row?.job_kinds ?? ['tavi']).join(', '));
  const [keyHeader, setKeyHeader] = useState(row?.key_header ?? '');
  const [timeout, setTimeoutValue] = useState(String(row?.timeout_seconds ?? 120));
  const [notes, setNotes] = useState(row?.notes ?? '');

  let bodyValue: unknown = null;
  let bodyError: string | null = null;
  if (body.trim()) {
    try {
      bodyValue = JSON.parse(body);
      if (typeof bodyValue !== 'object') bodyError = 'A JSON object or array.';
    } catch {
      bodyError = 'Not valid JSON.';
    }
  }
  const ordinalNumber = Number(ordinal);
  const timeoutNumber = Number(timeout);
  const valid = STEP_KEY.test(key) && Number.isInteger(ordinalNumber) && Number.isInteger(timeoutNumber) &&
    timeoutNumber >= 5 && timeoutNumber <= 900 && !bodyError;

  return (
    <Modal title={row ? `Edit ${row.key}` : 'Add a step'} size="lg" onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!valid} onClick={() => onSave({
            ...(row ?? {}),
            ordinal: ordinalNumber,
            key: key.trim(),
            kind,
            app: app.trim() || null,
            method: method.trim() || null,
            url_template: url.trim() || null,
            body_template: bodyValue,
            enabled,
            skip_if_absent: skip,
            job_kinds: jobKinds.split(',').map((k) => k.trim()).filter(Boolean),
            key_header: keyHeader || null,
            timeout_seconds: timeoutNumber,
            notes: notes.trim() || null,
          })}>Apply</button>
        </>
      }>
      <p className="t-caption muted" style={{ margin: 0 }}>
        Applied to the draft; nothing is saved until “Save catalog”. Template variables: {vocabulary.map((v) => `{${v}}`).join(' ')}
      </p>
      <div className="form-grid">
        <TextField label="Ordinal" value={ordinal} onChange={setOrdinal} type="number" />
        <TextField label="Key" value={key} onChange={setKey} mono disabled={!!row && REQUIRED_KEYS.includes(row.key)}
          error={key && !STEP_KEY.test(key) ? '^[a-z][a-z0-9_.]{1,63}$' : null} />
        <Select label="Kind" value={kind} onChange={setKind} options={KINDS.map((k) => ({ value: k, label: k }))} searchable={false} />
        <TextField label="Service (app)" value={app} onChange={setApp} mono placeholder="salesiq" />
        <TextField label="Method" value={method} onChange={setMethod} mono />
        <Select label="Key header" value={keyHeader} onChange={setKeyHeader} placeholder="X-Internal-Api-Key (default)" searchable={false}
          options={[{ value: 'X-Admin-Api-Key', label: 'X-Admin-Api-Key' }]} />
        <TextField label="Timeout (s)" value={timeout} onChange={setTimeoutValue} type="number" min={5} max={900} />
        <TextField label="Job kinds" value={jobKinds} onChange={setJobKinds} mono hint="tavi, external" />
      </div>
      <TextField label="URL template" value={url} onChange={setUrl} mono placeholder="/api/v1/{app}/internal/tenants/{tenant_id}/provision" />
      <TextAreaField label="Body template (JSON, optional)" value={body} onChange={setBody} mono rows={3} error={bodyError} />
      <div className="row">
        <CheckboxField label="Enabled" checked={enabled} onChange={setEnabled} disabled={!!row && REQUIRED_KEYS.includes(row.key)} />
        <CheckboxField label="Skip if the service is absent" checked={skip} onChange={setSkip} />
      </div>
      <TextField label="Notes" value={notes} onChange={setNotes} />
    </Modal>
  );
}
